
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { format } from 'date-fns';
import { nb } from 'date-fns/locale';
import EditorialLayout from '@/components/editorial/EditorialLayout';

const EmailLogsPage = () => {
  const { data: emailLogs, isLoading, error } = useQuery({
    queryKey: ['emailLogs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('email_logs')
        .select(`
          *,
          interview_requests (
            interviewee_name,
            question
          )
        `)
        .order('sent_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching email logs:', error);
        throw error;
      }
      
      return data;
    }
  });

  const [selectedEmail, setSelectedEmail] = React.useState<typeof emailLogs[0] | null>(null);

  if (isLoading) {
    return (
      <EditorialLayout>
        <div className="max-w-7xl mx-auto py-8 px-4">
          <h1 className="text-2xl font-bold mb-4">E-postlogger</h1>
          <div>Laster...</div>
        </div>
      </EditorialLayout>
    );
  }

  if (error) {
    return (
      <EditorialLayout>
        <div className="max-w-7xl mx-auto py-8 px-4">
          <h1 className="text-2xl font-bold mb-4">E-postlogger</h1>
          <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-700">
            <p className="font-medium">Kunne ikke laste e-postlogger</p>
            <p className="text-sm mt-1">Vennligst kontroller at du har tillatelse til å se disse dataene.</p>
          </div>
        </div>
      </EditorialLayout>
    );
  }

  return (
    <EditorialLayout>
      <div className="max-w-7xl mx-auto py-8 px-4">
        <h1 className="text-2xl font-bold mb-4">E-postlogger</h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            {emailLogs && emailLogs.length > 0 ? (
              emailLogs.map((log) => (
                <Card 
                  key={log.id} 
                  className={`p-4 cursor-pointer transition-colors ${
                    selectedEmail?.id === log.id ? 'border-primary' : ''
                  }`}
                  onClick={() => setSelectedEmail(log)}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">{log.subject}</p>
                      <p className="text-sm text-gray-600">{log.recipient_email}</p>
                      <p className="text-sm text-gray-500">
                        {format(new Date(log.sent_at), 'PPpp', { locale: nb })}
                      </p>
                    </div>
                    <div className="text-sm">
                      <span className={`px-2 py-1 rounded-full ${
                        log.status === 'sent' ? 'bg-green-100 text-green-800' : 'bg-gray-100'
                      }`}>
                        {log.status}
                      </span>
                    </div>
                  </div>
                </Card>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                Ingen e-postlogger funnet
              </div>
            )}
          </div>
          
          <div className="lg:sticky lg:top-4">
            {selectedEmail ? (
              <Card className="p-6">
                <div className="space-y-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-lg font-medium">{selectedEmail.subject}</h3>
                      <p className="text-gray-600">{selectedEmail.recipient_email}</p>
                      <p className="text-sm text-gray-500">
                        {format(new Date(selectedEmail.sent_at), 'PPpp', { locale: nb })}
                      </p>
                    </div>
                    <Button 
                      variant="ghost"
                      onClick={() => setSelectedEmail(null)}
                    >
                      Lukk
                    </Button>
                  </div>
                  
                  <div className="border rounded-lg p-4 bg-white">
                    <div 
                      dangerouslySetInnerHTML={{ __html: selectedEmail.html_content }} 
                    />
                  </div>
                  
                  {selectedEmail.metadata && (
                    <div className="mt-4">
                      <h4 className="font-medium mb-2">Metadata</h4>
                      <pre className="bg-gray-50 p-4 rounded-lg overflow-auto text-sm">
                        {JSON.stringify(selectedEmail.metadata, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              </Card>
            ) : (
              <div className="text-center text-gray-500 p-8">
                Velg en e-post for å se detaljer
              </div>
            )}
          </div>
        </div>
      </div>
    </EditorialLayout>
  );
};

export default EmailLogsPage;
